import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { supabase } from './supabase';
import StaffPortal from './pages/StaffPortal';
import ClientUpload from './pages/ClientUpload';
import DeliveryPortal from './pages/DeliveryPortal';
import { useIdleLogout } from './hooks/useIdleLogout';
import { recordSignout, readSignout, clearSignout, isDeliberate, signoutMessage } from './utils/signoutReason';
import logo from './assets/wigley-logo.png';

export default function App() {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  // ═══ A SESSION THAT ENDS UNEXPECTEDLY KEEPS THE SCREEN (Philip, 2026-10-10 — docs/signout-investigation.md) ═══
  // A SIGNED_OUT nobody asked for (the server's time limit, a refused refresh, a sign-out on another device) used to
  // swap the whole app for LoginScreen, unmounting the form being typed into. Now the screen stays mounted under a
  // sign-in box (ReauthOverlay) that hides it completely; signing back in as the SAME user closes the box and the
  // unsaved entries are still there to Save. A DIFFERENT user signing in reloads the page clean, so nobody
  // inherits someone else's open form. A deliberate sign-out (Sign out; 60 min idle in every tab — recorded by
  // utils/signoutReason before signOut) goes to LoginScreen as before, which says why.
  const [keptUser, setKeptUser] = useState(null);       // the user whose screen is kept under the sign-in box
  const [keptSignedInAt, setKeptSignedInAt] = useState(null);
  const [notice, setNotice] = useState('');             // LoginScreen's "why you were signed out"
  const sessionRef = useRef(null);
  const keptRef = useRef(null);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      sessionRef.current = session;
      setSession(session);
      if (!session) { const rec = readSignout(); setNotice(rec ? signoutMessage(rec.reason) : ''); }
      setLoading(false);
    });
    // ⛔ DIAGNOSTIC (added 2026-08-21): Philip is being ejected mid-work at irregular intervals. This handler
    // used to take (_event, session) and discard the event name, so a FAILED TOKEN REFRESH and a deliberate
    // sign-out were indistinguishable — both arrive as a session of null and both drop straight to
    // LoginScreen. supabase-js refreshes in a background timer with autoRefreshToken (default on, see
    // supabase.js — no options are passed); when that refresh fails it clears the session and emits
    // SIGNED_OUT. Nothing in this app observes the refresh, so the failure and its consequence are the same
    // event with nothing in between.
    //
    // Intermittent means one occurrence will not be enough, so this logs EVERY transition and records the
    // things that would distinguish the candidates across several:
    //   online          — a network blip during the refresh
    //   visibility      — a laptop waking or a tab regaining focus (a strong candidate for "random")
    //   upSeconds       — time since page load; a consistent value across occurrences means a CADENCE
    //                     (refresh failing at token expiry) rather than the randomness it feels like
    // ⛔ The event is NOT filtered and SIGNED_OUT is NOT suppressed: setSession stays unconditional, because
    // leaving a genuinely signed-out user on screen would be the same bug inverted. This only observes.
    // Remove once the cause is named.
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      const info = {
        event,
        hasSession: !!session,
        at: new Date().toISOString(),
        online: typeof navigator !== 'undefined' ? navigator.onLine : null,
        visibility: typeof document !== 'undefined' ? document.visibilityState : null,
        upSeconds: Math.round((typeof performance !== 'undefined' ? performance.now() : 0) / 1000),
      };
      // A transition that LOSES the session is the one worth spotting in a busy console.
      if (session) console.info('[auth]', info); else console.warn('[auth] SESSION LOST →', info);
      if (session) {
        if (keptRef.current && session.user?.id !== keptRef.current.id) { window.location.reload(); return; }
        keptRef.current = null; setKeptUser(null); setKeptSignedInAt(null);
        clearSignout(); setNotice('');
        sessionRef.current = session; setSession(session);
        return;
      }
      const had = sessionRef.current;
      sessionRef.current = null;
      const rec = readSignout();
      if (had && !isDeliberate(rec)) {
        console.warn('[auth] session ended unexpectedly — keeping the screen under the sign-in box', { reason: rec?.reason || null });
        keptRef.current = had.user;
        setKeptUser(had.user);
        setKeptSignedInAt(Date.parse(had.user?.last_sign_in_at || '') || null);
      } else if (!keptRef.current) {
        setNotice(rec ? signoutMessage(rec.reason) : '');
      }
      setSession(null);
    });
    return () => subscription.unsubscribe();
  }, []);

  // Neutralize the browser's native "Backspace = navigate back" behavior.
  // In legacy/embedded browsers, a Backspace while focus is on a non-editable
  // element (a row <div>/<td>, a button, body) walks history back — which then
  // surfaces through StaffPortal's popstate listener as an accidental page jump.
  // Backspace stays fully functional inside editable fields; everywhere else it's a no-op.
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key !== 'Backspace') return;
      const t = e.target;
      const tag = t && t.tagName;
      const editable =
        tag === 'INPUT' ||
        tag === 'TEXTAREA' ||
        (t && t.isContentEditable);
      if (!editable) e.preventDefault();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  // Idle auto-logout (60 min) with a 1-minute warning — only while signed in. Hook runs every render
  // (no early return before it) per Rules of Hooks; it no-ops when `enabled` is false.
  // Every sign-out the app makes records why first (utils/signoutReason), so each tab can tell it from a server one.
  const signOut = useCallback(() => { recordSignout('manual'); return supabase.auth.signOut(); }, []);
  const idleSignOut = useCallback(() => { recordSignout('idle'); return supabase.auth.signOut(); }, []);
  const { warningOpen, secondsLeft, staySignedIn } = useIdleLogout(!!session && !loading, idleSignOut);
  // ⛔ ONE USER OBJECT PER USER. supabase hands over a new session.user on every token refresh (about hourly), and
  // StaffPortal's main load runs on [user] — so it re-ran every hour, and would again on each change between the
  // live user and the kept one. Keyed on the id, the screen keeps its state across both.
  const currentUser = session?.user || keptUser;
  const stableUser = useMemo(() => currentUser, [currentUser?.id]);

  const path = window.location.pathname;

  // ── Public routes (no auth required) ──
  if (path.startsWith('/upload/')) {
    const token = path.replace('/upload/', '');
    return <ClientUpload token={token} />;
  }

  if (path.startsWith('/delivery/')) {
    const token = decodeURIComponent(path.replace('/delivery/', ''));
    return <DeliveryPortal token={token} />;
  }

  if (loading) {
    return (
      <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'100vh', background:'var(--bg)', fontSize:'14px', color:'var(--slate)' }}>
        Loading WigleyHub...
      </div>
    );
  }

  if (!session && !keptUser) {
    return <LoginScreen notice={notice} />;
  }

  // StaffPortal stays at the SAME position in this tree whether the session is live or kept, so React keeps it
  // (and every unsaved entry in it) mounted while the sign-in box is up.
  return (
    <>
      <StaffPortal user={stableUser} onSignOut={signOut} />
      {warningOpen && session && <IdleWarningModal secondsLeft={secondsLeft} onStay={staySignedIn} onSignOut={signOut} />}
      {!session && keptUser && <ReauthOverlay user={keptUser} message={signoutMessage('ended', { signedInAt: keptSignedInAt })} />}
    </>
  );
}

// The sign-in box over a kept screen. OPAQUE on purpose: the session has ended, so nothing behind it may be read or
// clicked — and nothing can be saved without a session anyway (src/utils/authFetch stops every request first).
function ReauthOverlay({ user, message }) {
  return (
    <div role="dialog" aria-modal="true"
      style={{ position:'fixed', inset:0, background:'var(--bg,#f6f4ef)', zIndex:2147483100, display:'flex', alignItems:'center', justifyContent:'center', padding:'20px' }}>
      <div style={{ background:'var(--panel,#fff)', border:'1px solid var(--line,#e5e5e5)', borderRadius:'12px', padding:'32px', width:'100%', maxWidth:'420px', boxShadow:'0 8px 40px rgba(0,0,0,0.18)' }}>
        <div style={{ fontSize:'16px', fontWeight:700, color:'var(--ink,#1a1a1a)', marginBottom:'8px' }}>Sign in to carry on</div>
        <div style={{ fontSize:'13px', color:'var(--slate,#666)', marginBottom:'6px', lineHeight:1.5 }}>{message}</div>
        <div style={{ fontSize:'13px', color:'var(--ink,#1a1a1a)', marginBottom:'18px', lineHeight:1.5 }}>
          <b>Your screen and any unsaved entries are kept.</b> Sign in again, then save as usual.
        </div>
        <LoginForm defaultEmail={user?.email || ''} />
        <button type="button" onClick={() => window.location.reload()}
          style={{ marginTop:'12px', width:'100%', padding:'9px', background:'transparent', border:'1px solid var(--line,#ccc)', borderRadius:'8px', color:'var(--slate,#666)', fontSize:'12px', cursor:'pointer' }}>
          Discard the screen and go to the sign-in page
        </button>
      </div>
    </div>
  );
}

// Idle-logout warning — backdrop-locked (no outside-click dismiss, so it can't be accidentally closed
// like the §13.5 modal drag-close bug), high z-index, centered. "Stay signed in" is autofocused so
// Enter keeps the session. Reaching this at all means 59 min of no activity; typing never triggers it.
function IdleWarningModal({ secondsLeft, onStay, onSignOut }) {
  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.6)', zIndex:2147483000, display:'flex', alignItems:'center', justifyContent:'center', padding:'20px' }}>
      <div style={{ background:'var(--panel,#fff)', border:'1px solid var(--line,#e5e5e5)', borderRadius:'12px', padding:'28px', width:'100%', maxWidth:'400px', boxShadow:'0 8px 40px rgba(0,0,0,0.28)', textAlign:'center' }}>
        <div style={{ fontSize:'32px', marginBottom:'6px' }}>⏳</div>
        <div style={{ fontSize:'16px', fontWeight:700, color:'var(--ink,#1a1a1a)', marginBottom:'8px' }}>Still there?</div>
        <div style={{ fontSize:'13px', color:'var(--slate,#666)', marginBottom:'20px', lineHeight:1.5 }}>
          You'll be signed out for inactivity in <b style={{ color:'#B40000' }}>{Math.max(secondsLeft, 0)}s</b>.<br/>Any unsaved changes will be lost.
        </div>
        <div style={{ display:'flex', gap:'10px' }}>
          <button type="button" onClick={onSignOut} style={{ flex:1, padding:'11px', background:'transparent', border:'1px solid var(--line,#ccc)', borderRadius:'8px', color:'var(--slate,#666)', fontSize:'13px', fontWeight:600, cursor:'pointer' }}>Sign out now</button>
          <button type="button" onClick={onStay} autoFocus style={{ flex:2, padding:'11px', background:'var(--red,#B40000)', border:'none', borderRadius:'8px', color:'#fff', fontSize:'13px', fontWeight:700, cursor:'pointer' }}>Stay signed in</button>
        </div>
      </div>
    </div>
  );
}

function LoginScreen({ notice }) {
  return (
    <div style={{ minHeight:'100vh', background:'var(--bg)', display:'flex', alignItems:'center', justifyContent:'center' }}>
      <div style={{ background:'var(--panel)', border:'1px solid var(--line)', borderRadius:'var(--radius)', padding:'40px', width:'100%', maxWidth:'420px', boxShadow:'var(--shadow)' }}>
        <div style={{ textAlign:'center', marginBottom:'32px' }}>
          <img src={logo} alt="Wigley" style={{ height:'72px', width:'auto', objectFit:'contain', marginBottom:'8px' }} />
          <div style={{ fontSize:'12px', color:'var(--slate)' }}>National Tax Resource Group</div>
        </div>
        {notice && <div style={{ fontSize:'12px', color:'var(--ink)', marginBottom:'16px', padding:'9px 12px', background:'var(--red-tint)', borderRadius:'6px', lineHeight:1.5 }}>{notice}</div>}
        <LoginForm />
      </div>
    </div>
  );
}

// The email + password form — the login page's, and the sign-in box's over a kept screen.
function LoginForm({ defaultEmail = '' }) {
  const [email, setEmail] = useState(defaultEmail);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  async function handleLogin(e) {
    e.preventDefault();
    setLoading(true); setError('');
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) setError(error.message);
    setLoading(false);
  }

  return (
        <form onSubmit={handleLogin}>
          <div style={{ marginBottom:'14px' }}>
            <input type="email" placeholder="Email address" value={email} onChange={e=>setEmail(e.target.value)} required autoComplete="username"
              style={{ width:'100%', padding:'11px 14px', background:'var(--panel)', border:'1px solid var(--line)', borderRadius:'8px', fontSize:'14px', color:'var(--ink)', boxSizing:'border-box', outline:'none' }} />
          </div>
          <div style={{ marginBottom:'20px' }}>
            <input type="password" placeholder="Password" value={password} onChange={e=>setPassword(e.target.value)} required autoComplete="current-password"
              style={{ width:'100%', padding:'11px 14px', background:'var(--panel)', border:'1px solid var(--line)', borderRadius:'8px', fontSize:'14px', color:'var(--ink)', boxSizing:'border-box', outline:'none' }} />
          </div>
          {error && <div style={{ fontSize:'12px', color:'#B40000', marginBottom:'14px', padding:'8px 12px', background:'var(--red-tint)', borderRadius:'6px' }}>{error}</div>}
          <button type="submit" disabled={loading}
            style={{ width:'100%', padding:'12px', background:'var(--red)', border:'none', borderRadius:'8px', color:'white', fontSize:'14px', fontWeight:'700', cursor:'pointer' }}>
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>
  );
}
